import { queryClient } from './queryClient'
import { qk } from './queryKeys'

/**
 * Task mutations ripple into many read surfaces: the task lists, task detail,
 * the approvals queue, the portfolio rollups, and the dashboard/insights
 * metrics. Invalidate the prefixes that cover all of them.
 */
export function invalidateTasks(): void {
  queryClient.invalidateQueries({ queryKey: qk.tasks.all() })
  queryClient.invalidateQueries({ queryKey: qk.approvals() })
  queryClient.invalidateQueries({ queryKey: qk.portfolio() })
  queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  queryClient.invalidateQueries({ queryKey: ['insights'] })
}

/** Project mutations affect the project list/detail, portfolio, and dashboard. */
export function invalidateProjects(): void {
  queryClient.invalidateQueries({ queryKey: qk.projects.all() })
  queryClient.invalidateQueries({ queryKey: qk.portfolio() })
  queryClient.invalidateQueries({ queryKey: ['dashboard'] })
}

export function invalidateTaskReminders(taskId: number): void {
  queryClient.invalidateQueries({ queryKey: qk.tasks.reminders(taskId) })
  queryClient.invalidateQueries({ queryKey: qk.reminders() })
}

export function invalidateTaskSubscribers(taskId: number): void {
  queryClient.invalidateQueries({ queryKey: qk.tasks.subscribers(taskId) })
}

/** Covers both the directory (`qk.org.directory`) and the shared unfiltered
 * picker cache (`qk.org.members`) — both keys live under the `['org', ...]`
 * prefix, so a member profile write refreshes every surface that reads it. */
export function invalidateOrgMembers(): void {
  queryClient.invalidateQueries({ queryKey: ['org'] })
}

export function invalidateHr(): void {
  queryClient.invalidateQueries({ queryKey: qk.hr.all() })
}
export function invalidateFinance(): void {
  queryClient.invalidateQueries({ queryKey: qk.finance.all() })
  queryClient.invalidateQueries({ queryKey: qk.requests.all() })
  queryClient.invalidateQueries({ queryKey: qk.operations.all() })
}

/** A project-plan write changes its graph, derived variance report and the
 * expense-coding choices. Invalidating the finance root intentionally also
 * refreshes parent budget rollups and request history. */
export function invalidateProjectBudgetPlan(budgetId: number): void {
  invalidateFinance()
  queryClient.invalidateQueries({ queryKey: qk.finance.projectPlan(budgetId) })
  queryClient.invalidateQueries({ queryKey: ['finance', 'project-budget-report'] })
  queryClient.invalidateQueries({ queryKey: ['finance', 'expense-budget-options'] })
}

export function invalidateRequests(): void {
  queryClient.invalidateQueries({ queryKey: qk.requests.all() })
}
export function invalidateTeams(): void {
  queryClient.invalidateQueries({ queryKey: qk.teams.all() })
}
export function invalidateSchedules(): void {
  queryClient.invalidateQueries({ queryKey: qk.schedules.all() })
}

/**
 * Bridge the legacy `window` event bus into React Query invalidation.
 *
 * WHY THIS EXISTS: the offline path (`offline/useOfflineTasks.ts`) still
 * dispatches/listens to `granula:tasks-changed` — that dispatch must keep
 * firing (AppShell's CreateTaskModal) so the offline cache resyncs, and this
 * bridge lets it also refresh the React Query cache without a second call
 * site. `granula:projects-changed` has no dispatcher left as of T4 (every
 * write site now calls `invalidateProjects()` directly) — the listener stays
 * registered here anyway since it's a no-cost no-op and splitting the two
 * event names into separate functions isn't worth the complexity.
 *
 * Mount once, at app root, for the app's lifetime.
 */
export function installLegacyEventBridge(): () => void {
  const onTasks = () => invalidateTasks()
  const onProjects = () => invalidateProjects()
  const onFinance = () => invalidateFinance()
  window.addEventListener('granula:tasks-changed', onTasks)
  window.addEventListener('granula:projects-changed', onProjects)
  window.addEventListener('granula:finance-changed', onFinance)
  window.addEventListener('granula:expenses-changed', onFinance)
  return () => {
    window.removeEventListener('granula:tasks-changed', onTasks)
    window.removeEventListener('granula:projects-changed', onProjects)
    window.removeEventListener('granula:finance-changed', onFinance)
    window.removeEventListener('granula:expenses-changed', onFinance)
  }
}
